import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_SIZES } from "@/components_v2/Button/constants";
import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import { useGetAdminContributionGradesQuery } from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import {
  CERTIFICATION_LIFECYCLE_STATUSES,
  CONTRIBUTION_GRADE_STATES,
} from "@/domains/certifications/constants";
import { ADMIN_TABS } from "@/domains/certifications/routes";
import AdminStats from "@/features/certifications/admin/components/AdminStats";
import AnalyticsTab from "@/features/certifications/analytics";
import ParticipantsTab from "@/features/certifications/participants";
import InvitationsTab from "@/features/certifications/invitations";
import InviteModal from "@/features/certifications/invitations/components/InviteModal";
import CommunityFlagsTab from "@/features/certifications/community-flags";
import KnowledgeCheckFlagsTab from "@/features/certifications/knowledge-check-flags";
import { CertificationAdminProps } from "@/features/certifications/admin/types";

import "./styles.css";

const START_DATE_FORMAT = "MMMM yyyy";

const CertificationAdmin = ({
  certification,
  onShowInvitations,
}: CertificationAdminProps) => {
  const { t } = useTranslation();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const pendingGrades = useGetAdminContributionGradesQuery({
    certification: certification.id,
    state: CONTRIBUTION_GRADE_STATES.PENDING_HUMAN_REVIEW,
    pagination: "off",
  });
  const flagCount = pendingGrades.data
    ? isPaginatedResponse(pendingGrades.data)
      ? pendingGrades.data.count
      : pendingGrades.data.length
    : 0;
  const startsAt = certification.startsAt
    ? DateTime.fromISO(certification.startsAt)
    : null;

  const tabs = useMemo(
    () => [
      {
        key: ADMIN_TABS.PARTICIPANTS,
        label: t("certification.admin.tabs.participants"),
        children: <ParticipantsTab certification={certification} />,
      },
      {
        key: ADMIN_TABS.INVITATIONS,
        label: t("certification.admin.tabs.invitations"),
        children: <InvitationsTab certification={certification} />,
      },
      {
        key: ADMIN_TABS.COMMUNITY_FLAGS,
        label: (
          <span className="flex items-center gap-100">
            {t("certification.admin.tabs.communityFlags")}

            {flagCount > 0 && (
              <span className="certification-admin-tab-count body-s-regular">
                {flagCount}
              </span>
            )}
          </span>
        ),
        children: <CommunityFlagsTab certification={certification} />,
      },
      {
        key: ADMIN_TABS.KNOWLEDGE_CHECK_FLAGS,
        label: t("certification.admin.tabs.knowledgeCheckFlags"),
        children: <KnowledgeCheckFlagsTab certification={certification} />,
      },
      {
        key: ADMIN_TABS.ANALYTICS,
        label: t("certification.admin.tabs.analytics"),
        children: <AnalyticsTab certification={certification} />,
      },
    ],
    [certification, flagCount, t],
  );

  return (
    <div className="certification-admin flex flex-col gap-500">
      <header className="flex flex-wrap items-start justify-between gap-300">
        <div className="flex flex-col gap-100">
          <span className="certification-admin-eyebrow body-xs-regular">
            {certification.title}
          </span>

          <h1 className="certification-admin-title header-m-medium">
            {t("certification.admin.title")}
          </h1>

          {startsAt && (
            <span className="certification-admin-subline body-s-regular">
              {t(
                certification.status ===
                  CERTIFICATION_LIFECYCLE_STATUSES.UPCOMING
                  ? "certification.admin.startingIn"
                  : "certification.admin.startedIn",
                { date: startsAt.toFormat(START_DATE_FORMAT) },
              )}
            </span>
          )}
        </div>

        <BaseButton
          size={BUTTON_SIZES.SMALL}
          onClick={() => setIsInviteOpen(true)}
        >
          {t("certification.admin.invite")}
        </BaseButton>
      </header>

      <AdminStats certification={certification} />

      <BaseTabs items={tabs} />

      <InviteModal
        open={isInviteOpen}
        certification={certification}
        onClose={() => setIsInviteOpen(false)}
        onInvited={() => {
          setIsInviteOpen(false);
          onShowInvitations();
        }}
      />
    </div>
  );
};

export default CertificationAdmin;
